import { Button, ButtonGroup, Checkbox, Container, Dialog, DialogTitle, FormControlLabel, FormGroup, Grid, List, ListItem, ListItemText, Paper, Popover, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { EkasiServeAppBar, equalArrayOfArrayOfObj, equalArrayOfObj, formatPercentage, LoadingPage, selectAllOnFocus, SetState } from "./Common";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { calculateFinancialSummary, DefaultFinancialSummary, FinancialSummary, Item, mapDbItemsToLocal } from "./Items";
import { useParams } from "react-router-dom";
import { Quote, QuoteEditPreviewButton } from "./Quotes";
import { ArrowDropDownIcon, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Company } from "./Companies";
import { Client } from "./Clients";
import { ClientDetails } from "./ClientDetails";
import { CreditHTML, DeliveryHTML, DownloadPDFButton, InvoiceHTML, pdfFileName } from "./PDFs/TemplatesGen";
import { UserContext, userIsEmployeeOrAdmin } from "./Users";

export interface ItemDelivery {
    quote_no: string;
    description: string;
    item_size: string;
    iteration: number;
    delivery_amount: number;
}

export interface ItemDeliveryDate {
    quote_no: string;
    iteration: number;
    tax_date: string;
    paid: number;
    total_price: number;
    total_cost: number;
}

export function mergeItemDeliveryDates(old: Array<ItemDeliveryDate>, toAdd: Array<ItemDeliveryDate>): Array<ItemDeliveryDate> {
    var result = [...old];
    for (let q of toAdd) {
        if (result.find((p) => p.quote_no === q.quote_no && p.iteration === q.iteration) === undefined) {
            result.push(q);
        }
    }
    return result;
}

function QuoteDeliveryForm({ iteration, setIteration, itemDeliveryDates, setItemDeliveryDates, addNewIteration, setRequestDelete } : { iteration: number, setIteration: SetState<number>, itemDeliveryDates: Array<ItemDeliveryDate>, setItemDeliveryDates: SetState<Array<ItemDeliveryDate>>, addNewIteration: () => void, setRequestDelete: SetState<boolean> }) {
    const auth = useContext(UserContext);
    const isEmployee = userIsEmployeeOrAdmin(auth)
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            {isEmployee && <Fragment>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ButtonGroup fullWidth>
                        <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Remove Delivery </Button>
                        <Button variant="contained" onClick={(ev) => addNewIteration()} fullWidth> New Delivery </Button>
                    </ButtonGroup>
                </Grid>
            </Fragment>}
            <Grid size={{ xs: 6, md: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Tax Date"
                        readOnly={!isEmployee}
                        slotProps={{ textField: { fullWidth: true } }}
                        onChange={(value) => setItemDeliveryDates(items => items.map((item, idx) => idx === iteration ? {...item, tax_date: value?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD")} : item) )}
                        value={(itemDeliveryDates[iteration]?.tax_date || "") === "" ? dayjs() : dayjs(itemDeliveryDates[iteration].tax_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
                {isEmployee && <FormGroup>
                    <FormControlLabel
                        checked={itemDeliveryDates[iteration]?.paid === 1}
                        control={<Checkbox />}
                        onChange={(e, checked) => setItemDeliveryDates(items => items.map((item, idx) => idx === iteration ? {...item, paid: checked ? 1 : 0} : item) )}
                        label="Paid" />
                </FormGroup>}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <ButtonGroup fullWidth>
                    <Button variant="outlined" disabled={iteration <= 0} onClick={(ev) => iteration > 0 ? setIteration(iteration - 1) : 0} fullWidth> Previous Delivery </Button>
                    <Button variant="outlined" disabled={iteration >= itemDeliveryDates.length - 1} onClick={(ev) => iteration < itemDeliveryDates.length - 1 ? setIteration(iteration + 1) : 0} fullWidth> Next Delivery </Button>
                </ButtonGroup>
            </Grid>
        </Grid>
    )
}

function unitsForQuoteItem(items: Array<ItemDelivery>, description: string, itemSize: string): number {
    for (let item of items) {
        if (item.description === description && item.item_size === itemSize) {
            return item.delivery_amount;
        }
    }
    return 0;
}

export function aggregateUnitCounts(items: Array<Item>, itemDeliveries: Array<Array<ItemDelivery>>, iteration: number): Array<ItemDelivery> {
    var result: Array<ItemDelivery> = [];
    for (let item of items) {
        result.push({ quote_no: item.quote_no, description: item.description, delivery_amount: item.units, item_size: item.item_size, iteration: 0 });
    }
    var i = 0;
    for (let itemsList of itemDeliveries) {
        if (i >= iteration) {
            break;
        }

        for (let item of itemsList) {
            for (let it in result) {
                if (result[it].description === item.description && result[it].item_size === item.item_size) {
                    result[it].delivery_amount -= item.delivery_amount;
                }
            }
        }

        i += 1;
    }

    return result;
}

function SaveQuotePopover({ saveQuote, edited, quote, details, client, company, items, summary, itemDeliveries, itemDeliveryDate, aggregateDelivery }: { saveQuote: any, edited: boolean, quote: Partial<Quote>, details: Partial<ClientDetails>, client: Partial<Client>, company: Partial<Company>, items: Array<Item>, summary: FinancialSummary, itemDeliveries: Array<ItemDelivery>, itemDeliveryDate: ItemDeliveryDate, aggregateDelivery: Array<ItemDelivery> }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    return (
        <Fragment>
            <ButtonGroup variant="contained" fullWidth>
                <Button disabled={!edited} variant="contained" onClick={saveQuote} fullWidth> Save </Button>
                <Button sx={{ width: 32 }} aria-describedby="Products" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained">
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Popover
                id="SaveQuote"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <List>
                    <ListItem>
                        <DownloadPDFButton content={DeliveryHTML(quote, details, client, items, aggregateDelivery)} filename={pdfFileName(quote, "Delivery Note", itemDeliveryDate)}>
                            <ListItemText>Export Delivery Note</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                    <ListItem>
                        <DownloadPDFButton content={InvoiceHTML(quote, details, client, company, items, summary, itemDeliveries, itemDeliveryDate)} filename={pdfFileName(quote, "Invoice", itemDeliveryDate)}>
                            <ListItemText>Export Tax Invoice</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                    <ListItem>
                        <DownloadPDFButton content={CreditHTML(quote, details, client, company, items, summary, itemDeliveries, itemDeliveryDate)} filename={pdfFileName(quote, "Credit", itemDeliveryDate)}>
                            <ListItemText>Export Credit Note</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export const QuoteDeliveryPage = () => {
    const { quoteNoText } = useParams();
    const quoteNo = quoteNoText === undefined || quoteNoText === "0" ? "" : quoteNoText;
    const auth = useContext(UserContext);

    const [quote, setQuote] = useState<Partial<Quote>>({});
    const [company, setCompany] = useState<Partial<Company>>({});
    const [client, setClient] = useState<Partial<Client>>({});
    const [clientDetail, setClientDetail] = useState<Partial<ClientDetails>>({});
    const [items, setItems] = useState<Array<Item>>([]);
    const [itemDeliveryDates, setItemDeliveryDates] = useState<Array<ItemDeliveryDate>>([]);
    const [itemDeliveries, setItemDeliveries] = useState<Array<Array<ItemDelivery>>>([]);
    const [iteration, setIteration] = useState<number>(0);
    const [aggregateDelivery, setAggregateDelivery] = useState<Array<ItemDelivery>>([]);
    const [aggregateDeliveryNote, setAggregateDeliveryNote] = useState<Array<ItemDelivery>>([]);
    const [financialSummary, setFinancialSummary] = useState<FinancialSummary>(DefaultFinancialSummary);
    const [edited, setEdited] = useState(false);
    const [savedItemDeliveries, setSavedItemDeliveries] = useState<Array<Array<ItemDelivery>>>([]);
    const [savedItemDeliveryDates, setSavedItemDeliveryDates] = useState<Array<ItemDeliveryDate>>([]);
    const [isLoading, setIsLoading] = useState(0);

    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);

    const addNewIteration = useCallback(() => {
        var itemDeliveries: Array<ItemDelivery> = [];
        for (let item of items) {
            itemDeliveries.push({
                quote_no: item.quote_no,
                description: item.description,
                item_size: item.item_size,
                iteration: itemDeliveryDates.length,
                delivery_amount: unitsForQuoteItem(aggregateDeliveryNote, item.description, item.item_size),
            });
        }
        setItemDeliveries(items => [...items, itemDeliveries]);
        setItemDeliveryDates(items => [...items, { quote_no: quote.quote_no || "", iteration: items.length, tax_date: dayjs().format("YYYY-MM-DD"), paid: 0, total_price: 0, total_cost: 0 }])
        setIteration(itemDeliveryDates.length);
    }, [itemDeliveryDates.length, items, quote.quote_no, aggregateDeliveryNote]);

    // Fetch Full Quote
    useEffect(() => {
        const fetchData = async () => {
            const quoteResponse = await fetch("/api/v1/quotes/" + encodeURIComponent(quoteNo));
            const quoteData = await quoteResponse.json();
            quoteData.result.quote.service_fee_text = formatPercentage(quoteData?.result?.quote?.service_fee || 250);
            quoteData.result.quote.vat_rate_text = formatPercentage(quoteData?.result?.quote?.vat_rate || 1500);            
            setQuote(quoteData?.result?.quote || {});
            setClient(quoteData?.result?.client || {});
            setClientDetail(quoteData?.result?.client_detail || {});
            if (quoteData.error) {
                setSnackbar(quoteData.error);
            }

            const itemsResponse = await fetch("/api/v1/items_delivered/" + encodeURIComponent(quoteNo));
            const itemsData = await itemsResponse.json();
            setItemDeliveries(itemsData?.result?.items || []);
            setItemDeliveryDates(itemsData?.result?.dates || []);
            setSavedItemDeliveries(itemsData?.result?.items || []);
            setSavedItemDeliveryDates(itemsData?.result?.dates || []);
            if (itemsData.error) {
                setSnackbar(itemsData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [quoteNo]);

    useEffect(() => {
        const fetchData = async () => {
            if (client.company_id !== undefined) {
                const companyResponse = await fetch("/api/v1/companies/" + encodeURIComponent(client.company_id || "0"));
                const companyData = await companyResponse.json();
                setCompany(companyData.result || {});
                if (companyData.error) {
                    setSnackbar(companyData.error);
                }
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [client.company_id]);

    // Update Quote Items
    useEffect(() => {
        const fetchData = async () => {
            const param = quoteNo !== undefined && quoteNo !== "" ? quoteNo : (quote.quote_no !== undefined ? quote.quote_no : "");
            if (param !== "") {
                const itemsResponse = await fetch("/api/v1/items/" + encodeURIComponent(param));
                const itemsData = await itemsResponse.json();
                setItems(itemsData?.result?.map(mapDbItemsToLocal) || []);
                if (itemsData.error) {
                    setSnackbar(itemsData.error);
                }
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [quoteNo, quote.quote_no]);

    useEffect(() => {
        setAggregateDelivery(aggregateUnitCounts(items, itemDeliveries, iteration));
        setAggregateDeliveryNote(aggregateUnitCounts(items, itemDeliveries, iteration + 1));
    }, [iteration, items, itemDeliveries]);

    useEffect(() => {
        calculateFinancialSummary(quote, items, setFinancialSummary, itemDeliveries[iteration] || [], setItemDeliveryDates, setSavedItemDeliveryDates, iteration);
    }, [quote, items, itemDeliveries, iteration]);

    useEffect(() => {
        setEdited(!(equalArrayOfArrayOfObj(itemDeliveries, savedItemDeliveries) && equalArrayOfObj(itemDeliveryDates, savedItemDeliveryDates)));
    }, [itemDeliveries, itemDeliveryDates, savedItemDeliveries, savedItemDeliveryDates]);


    const saveItems = useCallback(async () => {
        var response = await fetch("/api/v1/items_delivered/" + quoteNo, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({"items": itemDeliveries, "dates": itemDeliveryDates})
        });
        var data = await response.json();

        if (data.error) {
            setSnackbar(data.error);
        } else {
            setSavedItemDeliveries(itemDeliveries);
            setSavedItemDeliveryDates(itemDeliveryDates);
            setSnackbar("Information Saved");
        }
    }, [itemDeliveries, itemDeliveryDates, quoteNo]);

    const deleteItems = useCallback(async () => {
        const isGreater = iteration >= itemDeliveryDates.length - 1;
        setItemDeliveryDates(items => items.filter((item, idx) => idx !== iteration).map(item => item.iteration > iteration ? { ...item, iteration: item.iteration - 1} : item));
        setItemDeliveries(itemsList => itemsList.filter((items, idx) => idx !== iteration).map(items => items.map(item => item.iteration > iteration ? {...item, iteration: item.iteration - 1} : item)));
        if (isGreater) {
            setIteration(iteration - 1);
        }
        setRequestDelete(false);
    }, [itemDeliveryDates.length, iteration]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 12, lg: userIsEmployeeOrAdmin(auth) ? 8 : 10 }}> 
                    <h1 style={{ textAlign: "center" }}> 
                        {quote.quote_no} {iteration > 0 ? " - " + iteration : ""}
                    </h1> 
                </Grid>
                {userIsEmployeeOrAdmin(auth) && <Grid size={{ xs: 6, md: 6, lg: 2}}>
                    <SaveQuotePopover saveQuote={saveItems} edited={edited} quote={quote} details={clientDetail} client={client} company={company} items={items} summary={financialSummary} itemDeliveries={itemDeliveries[iteration]} itemDeliveryDate={itemDeliveryDates[iteration]} aggregateDelivery={aggregateDeliveryNote} />
                </Grid>}
                <Grid size={{ xs: 6, md: 6, lg: 2 }}>
                    <QuoteEditPreviewButton quote_no={quoteNo} iconOnly={true} />
                </Grid>
                
                
                <Grid size={12}>
                    <QuoteDeliveryForm 
                        iteration={iteration}
                        setIteration={setIteration}
                        itemDeliveryDates={itemDeliveryDates}
                        setItemDeliveryDates={setItemDeliveryDates}
                        addNewIteration={addNewIteration}
                        setRequestDelete={setRequestDelete}/>
                </Grid>
            </Grid>
            <Grid size={12} sx={{ paddingTop: 2 }}> </Grid>
            <Grid component={Container} container spacing={2}>
                <Grid component={Paper} size={12}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell>Item Size</TableCell>
                                <TableCell>Requested</TableCell>
                                <TableCell>Delivered</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                itemDeliveries[iteration]?.map((item, idx) => (
                                    <TableRow key={item.description + " " + item.item_size + " " + idx}>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>{item.item_size}</TableCell>
                                        <TableCell>{unitsForQuoteItem(aggregateDelivery, item.description, item.item_size)}</TableCell>
                                        <TableCell>
                                            {
                                            userIsEmployeeOrAdmin(auth)
                                                ? <TextField
                                                    id="delivery_amount"
                                                    label="Amount Delivered"
                                                    variant="outlined"
                                                    onFocus={selectAllOnFocus}
                                                    error={item.delivery_amount > unitsForQuoteItem(aggregateDelivery, item.description, item.item_size)}
                                                    slotProps={{ input: { readOnly: !userIsEmployeeOrAdmin(auth) } }}
                                                    onChange={(e) => setItemDeliveries(itemsList => itemsList.map((items, jdx) => jdx === iteration ? items.map((item, kdx) =>  kdx === idx ? { ...item, delivery_amount: parseInt(e.target.value || "0") } : item) : items))}
                                                    fullWidth
                                                    value={item.delivery_amount || "0"} />
                                                : <Typography>
                                                    {item.delivery_amount || "0"}
                                                </Typography>
                                            }
                                        </TableCell>
                                    </TableRow>
                                )) || <></>
                            }
                        </TableBody>
                    </Table>
                    
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this delivery instance? </DialogTitle>
                <Button onClick={deleteItems}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}